import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { API } from '../../API';
import { Loader } from '../../components/Loader/Loader';
import Modal from '../../components/Modal';
import { Paginator } from '../../components/Paginator/Paginator';
import Tooltip from '../../components/Tooltip/Tooltip';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { ReactComponent as PlusIcon } from '../../assets/plus.svg';

import {
  AdminAppListItem,
  AdminAppListResponse,
  RefreshSchemaResponse,
} from './adminTypes';

import styles from './WindmillAdminListPage.module.scss';

const PAGE_SIZE = 50;

export const WindmillAdminListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentUser] = useCurrentUser();
  const [searchParams] = useSearchParams();
  const page = Number(searchParams.get('page') || 1);

  const [data, setData] = useState<AdminAppListResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminAppListItem | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchApps = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('size', String(PAGE_SIZE));

    API.fetch('GET', `/API/v2/internal/windmill/apps?${params.toString()}`)
      .then((response: AdminAppListResponse) => {
        setData(response);
        setLoadError(null);
      })
      .catch((err: Error) => {
        setLoadError(err.message);
      });
  }, [page]);

  useEffect(() => {
    if (currentUser === null) return;
    if (!currentUser.is_admin && !currentUser.is_super_admin) {
      navigate('/');
      return;
    }
    fetchApps();
  }, [currentUser, fetchApps, navigate]);

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setBusyId(target.id);
    setDeleteTarget(null);
    API.fetch('DELETE', `/API/v2/internal/windmill/apps/${target.id}`)
      .then(() => {
        toast.success(t('App deleted') as string);
        fetchApps();
      })
      .catch((err: Error) => {
        toast.error(err.message);
      })
      .finally(() => setBusyId(null));
  }, [deleteTarget, fetchApps, t]);

  const handleRefreshSchema = useCallback(
    (app: AdminAppListItem) => {
      setBusyId(app.id);
      API.fetch(
        'POST',
        `/API/v2/internal/windmill/apps/${app.id}/refresh-schema`,
      )
        .then((resp: RefreshSchemaResponse) => {
          toast.success(
            t('Schema refreshed (v{{version}})', {
              version: resp.schema_version,
            }) as string,
          );
          fetchApps();
        })
        .catch((err: Error) => {
          toast.error(err.message);
        })
        .finally(() => setBusyId(null));
    },
    [fetchApps, t],
  );

  const handleToggleActive = useCallback(
    (app: AdminAppListItem) => {
      setBusyId(app.id);
      API.fetch('PATCH', `/API/v2/internal/windmill/apps/${app.id}`, null, {
        is_active: !app.is_active,
      })
        .then(() => {
          toast.success(
            (app.is_active ? t('App deactivated') : t('App activated')) as string,
          );
          fetchApps();
        })
        .catch((err: Error) => {
          toast.error(err.message);
        })
        .finally(() => setBusyId(null));
    },
    [fetchApps, t],
  );

  if (loadError) {
    return (
      <div className={styles.root}>
        <div className={styles.errorState}>{loadError}</div>
      </div>
    );
  }

  if (!data) {
    return <Loader />;
  }

  return (
    <div className="list-content">
      <div className="page-header">
        <div className="breadcrumb">
          <span>{t('Windmill Apps')}</span>
        </div>
        <div className="controls">
          <Link to="/windmill-admin/new" className="button new-button">
            <PlusIcon />
            {t('Create app')}
          </Link>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        {data.objects.length === 0 ? (
          <div className={styles.empty}>
            {t('No windmill apps yet. Create your first one.')}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">{t('Name')}</th>
                <th scope="col">{t('Slug')}</th>
                <th scope="col">{t('Category')}</th>
                <th scope="col">{t('Mode')}</th>
                <th scope="col">{t('Schema')}</th>
                <th scope="col">{t('Status')}</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {data.objects.map((app) => (
                <tr key={app.id} className={!app.is_active ? styles.inactiveRow : undefined}>
                  <td>
                    <div className={styles.nameCell}>
                      {app.icon && (
                        <span className={styles.icon}>{app.icon}</span>
                      )}
                      <div>
                        <div className={styles.name}>{app.name}</div>
                        {app.description && (
                          <div className={styles.description}>
                            {app.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <code className={styles.slug}>{app.slug}</code>
                  </td>
                  <td>{app.category || <span className={styles.dim}>—</span>}</td>
                  <td>
                    <span className={styles.modeBadge}>
                      {app.default_execution_mode}
                    </span>
                  </td>
                  <td>v{app.schema_version}</td>
                  <td>
                    <span
                      className={
                        app.is_active ? styles.statusActive : styles.statusInactive
                      }
                    >
                      {app.is_active ? t('Active') : t('Inactive')}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <Tooltip content={t('Refresh schema')} position="bottom">
                        <button
                          className="new-button"
                          onClick={() => handleRefreshSchema(app)}
                          disabled={busyId === app.id}
                          type="button"
                          aria-label={t('Refresh schema') as string}
                        >
                          <i className="las la-sync" />
                        </button>
                      </Tooltip>
                      <Tooltip
                        content={
                          app.is_active ? t('Deactivate') : t('Activate')
                        }
                        position="bottom"
                      >
                        <button
                          className="new-button"
                          onClick={() => handleToggleActive(app)}
                          disabled={busyId === app.id}
                          type="button"
                          aria-label={
                            (app.is_active
                              ? t('Deactivate')
                              : t('Activate')) as string
                          }
                        >
                          <i
                            className={
                              app.is_active
                                ? 'las la-toggle-on'
                                : 'las la-toggle-off'
                            }
                          />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('Edit')} position="bottom">
                        <Link
                          to={`/windmill-admin/${app.id}`}
                          className="new-button"
                          aria-label={t('Edit') as string}
                        >
                          <i className="lar la-edit" />
                        </Link>
                      </Tooltip>
                      <Tooltip content={t('Delete')} position="bottom">
                        <button
                          className="btn-danger"
                          onClick={() => setDeleteTarget(app)}
                          disabled={busyId === app.id}
                          type="button"
                          aria-label={t('Delete') as string}
                        >
                          <i className="las la-trash" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Paginator
        size={data.size}
        page={data.page}
        total={data.total}
        id=""
        scrollContainerId=""
      />

      <Modal
        isVisible={Boolean(deleteTarget)}
        title={t('Delete app')}
        content={
          <p>
            {t(
              'Delete "{{name}}"? This is a soft delete — executions stay intact but the app becomes unavailable.',
              { name: deleteTarget?.name || '' },
            )}
          </p>
        }
        footer={
          <>
            <button className="btn-danger" onClick={handleDelete}>
              {t('Delete')}
            </button>
            <button className="btn-reset" onClick={() => setDeleteTarget(null)}>
              {t('Cancel')}
            </button>
          </>
        }
        onClose={() => setDeleteTarget(null)}
        className=""
      />
    </div>
  );
};
